import { useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useUnit } from 'effector-react';
import { useQueryClient } from '@tanstack/react-query';
import { $user } from '@/models/user';
import { pushApi } from '@/api/push.api';
import { subscribeToPush } from './serviceWorker';

type ServiceWorkerPushMessage = {
  type?: string;
  chatId?: string;
};

const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;

export const PushNotificationsBridge = () => {
  const { keycloak } = useKeycloak();
  const user = useUnit($user);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!keycloak.authenticated || !user || !vapidPublicKey) return;

    let isCancelled = false;

    const syncSubscription = async () => {
      try {
        const subscription = await subscribeToPush(vapidPublicKey);
        if (!subscription || isCancelled) return;

        await pushApi.saveSubscription(subscription);
      } catch (error) {
        if (error instanceof Error) {
          console.warn('Push subscription skipped:', error.message);
          return;
        }

        console.error('Ошибка при подписке на push:', error);
      }
    };

    void syncSubscription();

    return () => {
      isCancelled = true;
    };
  }, [keycloak.authenticated, user]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleMessage = (event: MessageEvent<ServiceWorkerPushMessage>) => {
      if (event.data?.type !== 'CHAT_NEW_MESSAGE') return;

      queryClient.invalidateQueries({ queryKey: ['chats'] });
      queryClient.invalidateQueries({
        queryKey: event.data?.chatId ? ['messages', event.data.chatId] : ['messages'],
      });
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [queryClient]);

  return null;
};
